import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import NumberFormat from "react-number-format";
import { COLOR_TEXT, COLOR_APP } from "../../../constants";
import { convertCurrency } from "../../../helpers";
import "./OrderAddModal.css";

const OrderAddModal = ({
  show,
  onHide,
  stocksData,
  onSelectStock,
  productName,
  setProductName,
  amount,
  setAmount,
  stockAmount,
  price,
  setPrice,
  totalPrice,
  setTotalPrice,
  addToast,
  onSave,
  onCancel,
  loading,
  currency = "ກີບ" // Default to Lao Kip if not provided
}) => {
  const [isHovering, setIsHovering] = useState(false);

  // Calculate total price automatically when amount or price changes
  useEffect(() => {
    if (amount && price) {
      const calculatedTotal = parseFloat(amount) * parseFloat(price);
      setTotalPrice(calculatedTotal);
    }
  }, [amount, price, setTotalPrice]);

  // Check if the form is valid for submission
  const isFormInvalid = () => {
    // Check if any required fields are empty or invalid
    if (!productName || productName.trim() === '') return true;
    if (!amount || parseFloat(amount) <= 0) return true;
    if (!price || parseFloat(price) <= 0) return true;
    
    // All validations passed
    return false;
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onHide();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      dialogClassName="order-add-modal"
    >
      <Modal.Header className="border-0 pb-0" closeButton>
        <Modal.Title className="modal-title">ເພີ່ມລາຍການສັ່ງຊື້</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <div className="row mb-4">
          <div className="col-md-6 mb-3 mb-md-0">
            <Form.Group>
              <Form.Label className="form-label">ລະຫັດ CF</Form.Label>
              <div className="input-group">
                <input
                  type="text"
                  list="listItem"
                  placeholder="ເລືອກສິນຄ້າ"
                  onChange={(e) => onSelectStock(e)}
                  className="form-control custom-input"
                />
                <datalist id="listItem">
                  {stocksData.length > 0 &&
                    stocksData?.map((item, index) => {
                      if (item?.amount <= 0) return "";
                      return (
                        <option key={index} value={"ລະຫັດ: " + item?.cfMessage}>
                          {"ສິນຄ້າ: " + item?.name}, {"ຈຳນວນ: " + item?.amount},
                          {"ລາຄາ: " + item?.price}
                        </option>
                      );
                    })}
                </datalist>
              </div>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label className="form-label">ຊື່ສິນຄ້າ</Form.Label>
              <Form.Control
                type="text"
                placeholder="ກະລຸນາປ້ອນຊື່ສິນຄ້າ"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="custom-input"
              />
            </Form.Group>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-6 mb-3 mb-md-0">
            <Form.Group>
              <Form.Label className="form-label">ຈໍານວນ</Form.Label>
              <div className="quantity-input-container">
                <NumberFormat
                  name="amount"
                  className="form-control custom-input"
                  onChange={(e) => {
                    if (parseFloat(e.target.value) > stockAmount) {
                      addToast("ປ້ອນຈໍານວນທີ່ນ້ອຍກວ່າ: " + stockAmount, {
                        appearance: "error",
                        autoDismiss: true,
                      });
                      return;
                    }
                    setAmount(e.target.value);
                  }}
                  value={amount ?? 1}
                  placeholder="ຈໍານວນ"
                  thousandSeparator={true}
                  prefix={""}
                />
                <div className="quantity-controls">
                  <button 
                    type="button" 
                    className="quantity-btn"
                    onClick={() => {
                      const currentAmount = parseFloat(amount) || 0;
                      if (currentAmount > 1) {
                        setAmount(currentAmount - 1);
                      }
                    }}
                  >
                    -
                  </button>
                  <button 
                    type="button" 
                    className="quantity-btn"
                    onClick={() => {
                      const currentAmount = parseFloat(amount) || 0;
                      if (currentAmount < stockAmount) {
                        setAmount(currentAmount + 1);
                      } else {
                        addToast("ປ້ອນຈໍານວນທີ່ນ້ອຍກວ່າ: " + stockAmount, {
                          appearance: "error",
                          autoDismiss: true,
                        });
                      }
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
              {stockAmount > 0 && (
                <small className="stock-info">
                  ຈໍານວນໃນສາງ: <span className="stock-count">{stockAmount}</span>
                </small>
              )}
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label className="form-label">ລາຄາ</Form.Label>
              <div className="price-input-container">
                <NumberFormat
                  name="price"
                  onChange={(e) => setPrice(e?.target?.value)}
                  value={price}
                  className="form-control custom-input"
                  placeholder="0"
                  thousandSeparator={true}
                  prefix={""}
                />
                <div className="currency-indicator">
                  {convertCurrency(currency)}
                </div>
              </div>
            </Form.Group>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label className="form-label">ທັງໝົດ</Form.Label>
              <div className="price-input-container">
                <NumberFormat
                  name="totalPrice"
                  onChange={(e) => setTotalPrice(e?.target?.value)}
                  value={totalPrice}
                  className="form-control custom-input total-price"
                  placeholder="0"
                  thousandSeparator={true}
                  prefix={""}
                  disabled
                />
                <div className="currency-indicator total-currency">
                  {convertCurrency(currency)}
                </div>
              </div>
            </Form.Group>
          </div>
        </div>

        <div className="action-buttons">
          <Button
            variant="light"
            className="cancel-button"
            onClick={handleCancel}
          >
            ຍົກເລີກ
          </Button>
          <Button
            variant="primary"
            className="save-button"
            onMouseEnter={() => !isFormInvalid() && setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              background: isHovering ? 
                "linear-gradient(47deg, rgba(90,84,162,1) 0%, rgba(26,102,190,1) 100%)" : 
                isFormInvalid() ? "#b0b0b0" : COLOR_APP,
              transition: "background 0.3s ease",
              opacity: isFormInvalid() ? 0.7 : 1,
              cursor: isFormInvalid() ? "not-allowed" : "pointer"
            }}
            onClick={onSave}
            disabled={loading || isFormInvalid()}
            title={isFormInvalid() ? "ກະລຸນາກວດສອບວ່າຂໍ້ມູນຄົບຖ້ວນ" : ""}
          >
            {loading ? (
              <div className="spinner-container">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              "ບັນທຶກ"
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OrderAddModal;
